import {
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import { disableWorkerAccount } from "../services/worker/workerAccessLifecycle";

export async function archiveOldTestShifts() {
  const snap = await getDocs(
    collection(db, "shifts")
  );

  let count = 0;

  for (const d of snap.docs) {
    const data = d.data() || {};

    /*
     * CRITICAL SAFETY GATE
     *
     * A shift may only be archived automatically
     * when it is explicitly marked as test data
     * AND explicitly approved for test cleanup.
     *
     * testData alone is not sufficient.
     */
    const isExplicitTestShift =
      data.testData === true &&
      data.testArchiveEligible === true;

    if (!isExplicitTestShift) {
      continue;
    }

    /*
     * Already archived records do not need
     * to be processed again.
     */
    if (data.archived === true) {
      continue;
    }

    await updateDoc(
      doc(db, "shifts", d.id),
      {
        archived: true,

        archivedAt:
          serverTimestamp(),

        archivedReason:
          "Explicit test shift cleanup",

        previousStatus:
          data.status || "",

        previousLifecycleStatus:
          data.lifecycleStatus || "",

        archivedByTestCleanup: true,
      }
    );

    count++;
  }

  return count;
}

export async function archiveOldTestWorkers() {
  const snap = await getDocs(
    collection(db, "users")
  );

  let count = 0;

  for (const d of snap.docs) {
    const data = d.data() || {};

    const role = String(
      data.role ||
        data.platformRole ||
        ""
    )
      .trim()
      .toLowerCase();

    const isWorkerRole = [
      "worker",
      "nurse",
      "caregiver",
    ].includes(role);

    /*
     * CRITICAL SAFETY GATE
     *
     * testData alone is NOT enough because
     * the previous broken cleanup function
     * incorrectly wrote testData: true onto
     * legitimate worker accounts.
     *
     * A worker must now be explicitly marked
     * as safe for automated test cleanup.
     */
    const isExplicitTestWorker =
      data.testData === true &&
      data.testArchiveEligible === true;

    if (!isWorkerRole) continue;

    if (!isExplicitTestWorker) continue;

    if (data.archived === true) continue;

    const updates = {
        archived: true,

        archivedAt:
          serverTimestamp(),

        archivedReason:
          "Explicit test worker cleanup",

        previousStatus:
          data.status || "",

        status: "archived",
    };
    if (String(data.platformRole || data.role || "").toLowerCase() === "worker") {
      await disableWorkerAccount(d.id, updates);
    } else {
      await updateDoc(doc(db, "users", d.id), updates);
    }

    count++;
  }

  return count;
}

export async function restoreWorkersArchivedByBadCleanup() {
  const snap = await getDocs(
    collection(db, "users")
  );

  const users = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  const normalizeEmail = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const badCleanupCandidates =
    users.filter((user) =>
      user.archived === true &&
      user.testData === true &&
      user.archivedReason ===
        "Clean worker reset" &&
      user.tenantId === "dogebridge" &&
      user.facilityId === "bridge_rehab"
    );

  let count = 0;

  for (const user of badCleanupCandidates) {
    const email =
      normalizeEmail(user.email);

    /*
     * If another non-archived user document
     * already exists for this email, do not
     * restore this archived duplicate.
     */
    const activeDuplicateExists =
      users.some((other) =>
        other.id !== user.id &&
        other.archived !== true &&
        normalizeEmail(other.email) ===
          email
      );

    if (activeDuplicateExists) {
      continue;
    }

    /*
     * If multiple archived documents exist for
     * the same email, prefer a Firebase UID-style
     * document over a legacy email-as-document-ID
     * record.
     */
    const sameEmailCandidates =
      badCleanupCandidates.filter(
        (other) =>
          normalizeEmail(other.email) ===
          email
      );

    const preferredCandidate =
      sameEmailCandidates.find(
        (candidate) =>
          normalizeEmail(candidate.id) !==
          email
      ) ||
      sameEmailCandidates[0];

    if (
      preferredCandidate?.id !==
      user.id
    ) {
      continue;
    }

    const restoredStatus =
      String(
        user.previousStatus || ""
      ).trim() || "active";

    await updateDoc(
      doc(db, "users", user.id),
      {
        archived: false,
        testData: false,

        archivedAt: null,
        archivedReason: "",

        status: restoredStatus,

        restoredFromBadCleanup: true,
        restoredAt:
          serverTimestamp(),
      }
    );

    count++;
  }

  return count;
}

export async function repairRestoredWorkerStatuses() {
  const snap = await getDocs(
    collection(db, "users")
  );

  const users = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  let count = 0;

  for (const user of users) {
    const tenantId =
      normalize(user.tenantId);

    const facilityId =
      normalize(user.facilityId);

    const status =
      normalize(user.status);

    const onboardingStatus =
      normalize(user.onboardingStatus);

    const workerStatus =
      normalize(user.workerStatus);

    const complianceStatus =
      normalize(user.complianceStatus);

    const workerOperationalState =
      String(
        user.workerOperationalState || ""
      )
        .trim()
        .toUpperCase();

    /*
     * Only repair workers restored by our
     * one-time bad-cleanup recovery.
     */
    const isRepairCandidate =
      user.restoredFromBadCleanup === true &&
      tenantId === "dogebridge" &&
      facilityId === "bridge_rehab" &&
      user.archived !== true &&
      status === "archived";

    if (!isRepairCandidate) {
      continue;
    }

    /*
     * These are the same readiness signals
     * used by workerEligibility.js.
     */
    const hasReadySignal =
      onboardingStatus === "approved" ||
      workerStatus === "ready" ||
      workerOperationalState === "READY" ||
      complianceStatus === "ready" ||
      complianceStatus === "approved" ||
      user.onboardingCompleted === true ||
      user.readyForShifts === true;

    let restoredStatus = "onboarding";

    if (hasReadySignal) {
      restoredStatus = "active";
    }

    await updateDoc(
      doc(db, "users", user.id),
      {
        status: restoredStatus,

        statusRepairedAfterBadCleanup: true,
        statusRepairedAt:
          serverTimestamp(),
      }
    );

    count++;
  }

  return count;
}
